import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../AuthContext';
import { organizationsAPI, usersAPI } from '../../api';
import { formatDate } from '../../utils';
import { Card, Spinner, EmptyState, Button, Modal, Input, Select, Badge } from '../../components/ui';
import { Building2, Users, Plus, RefreshCw, Mail, ChevronRight, Trash2, Edit2, Eye, EyeOff } from 'lucide-react';

export const OrganizationsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMasterAdmin = user?.role === 'master_admin';

  // List state
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);

  // Sync / SMTP
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpResult, setSmtpResult] = useState(null);

  // Drill-down
  const [openOrgId, setOpenOrgId] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit modal
  const [editOrg, setEditOrg] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPublicSignup, setEditPublicSignup] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Delete confirm
  const [deleteOrg, setDeleteOrg] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Generic
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await organizationsAPI.list();
      setOrgs(response.data);
    } catch (err) {
      console.error('Failed to load organizations:', err);
      setError(t('organizationsPage.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    setError(null);
    setCreating(true);
    try {
      await organizationsAPI.create({ name: createName.trim(), settings: {} });
      setCreateName('');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('organizationsPage.errors.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleSyncFromKeycloak = async () => {
    setSyncMessage(null);
    setError(null);
    setSyncing(true);
    try {
      const res = await organizationsAPI.syncFromKeycloak();
      setSyncMessage(res.data?.message || t('organizationsPage.syncedFromKeycloak', { count: res.data?.created ?? 0 }));
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('organizationsPage.errors.syncFailed'));
    } finally {
      setSyncing(false);
    }
  };

  const handleTestSmtp = async () => {
    setSmtpResult(null);
    setError(null);
    setSmtpTesting(true);
    try {
      const res = await organizationsAPI.testSmtp();
      setSmtpResult(res.data);
    } catch (err) {
      setSmtpResult({ ok: false, error: err.response?.data?.detail || err.message || t('organizationsPage.errors.requestFailed') });
    } finally {
      setSmtpTesting(false);
    }
  };

  // Drill-down: open a gym
  const openOrg = async (org) => {
    setOpenOrgId(org.id);
    setDetailLoading(true);
    setOrgDetail(org);
    setOrgUsers([]);
    try {
      const [detailRes, usersRes] = await Promise.all([
        organizationsAPI.get(org.id),
        usersAPI.list(undefined, org.id),
      ]);
      setOrgDetail(detailRes.data);
      setOrgUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to load org detail:', err);
      setError(t('organizationsPage.errors.detailFailed'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeOrg = () => {
    setOpenOrgId(null);
    setOrgDetail(null);
    setOrgUsers([]);
  };

  // Edit
  const startEdit = (org) => {
    setEditOrg(org);
    setEditName(org.name);
    setEditPublicSignup(org.public_signup_enabled || false);
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editOrg) return;
    setError(null);
    setEditSubmitting(true);
    try {
      await organizationsAPI.update(editOrg.id, {
        name: editName.trim(),
        public_signup_enabled: editPublicSignup,
      });
      setEditOrg(null);
      await load();
      if (openOrgId === editOrg.id) {
        // Refresh detail if currently open
        openOrg({ id: editOrg.id });
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('organizationsPage.errors.updateFailed'));
    } finally {
      setEditSubmitting(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteOrg) return;
    setError(null);
    setDeleting(true);
    try {
      await organizationsAPI.delete(deleteOrg.id);
      setDeleteOrg(null);
      if (openOrgId === deleteOrg.id) closeOrg();
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('organizationsPage.errors.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="organizations-page">
      <div className="section-frame">
        <p className="hero-kicker">{t('organizationsPage.orgControl')}</p>
        <h1 className="mt-2 font-display text-5xl uppercase leading-[0.9] text-[var(--color-text)]">
          {isMasterAdmin ? t('organizationsPage.titleMaster') : t('organizationsPage.titleAdmin')}
        </h1>
        <p className="mt-3 text-sm leading-7 text-[var(--color-text-muted)]">
          {isMasterAdmin ? t('organizationsPage.subtitleMaster') : t('organizationsPage.subtitleAdmin')}
        </p>
      </div>

      {isMasterAdmin && (
        <Card className="editorial-panel p-4">
          <h3 className="mb-3 font-display text-2xl uppercase text-[var(--color-text)]">
            {t('organizationsPage.createOrganization')}
          </h3>
          <form onSubmit={handleCreateOrg} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <Input
                label={t('auth.name')}
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder={t('organizationsPage.namePlaceholder')}
              />
            </div>
            <Button type="submit" disabled={creating || !createName.trim()}>
              {creating ? <Spinner size="sm" /> : <Plus className="w-4 h-4 mr-1" />}
              {t('common.create')}
            </Button>
          </form>
          {error && <p className="mt-2 text-sm text-red-300">{error}</p>}
          <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
            <p className="text-sm text-[var(--color-text-muted)] mb-2">
              {t('organizationsPage.syncHint')}
            </p>
            <Button type="button" variant="secondary" onClick={handleSyncFromKeycloak} disabled={syncing}>
              {syncing ? <Spinner size="sm" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {t('organizationsPage.syncFromKeycloak')}
            </Button>
            {syncMessage && <p className="mt-2 text-sm text-green-300">{syncMessage}</p>}
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
            <p className="text-sm text-[var(--color-text-muted)] mb-2">
              {t('organizationsPage.smtpHint')}
            </p>
            <Button type="button" variant="secondary" onClick={handleTestSmtp} disabled={smtpTesting}>
              {smtpTesting ? <Spinner size="sm" /> : <Mail className="w-4 h-4 mr-1" />}
              {t('organizationsPage.testSmtp')}
            </Button>
            {smtpResult && (
              <pre className="mt-2 p-3 text-xs bg-white/[0.04] rounded overflow-auto max-h-32 border border-[var(--color-border)]">
                {smtpResult.ok ? (
                  <span className="text-green-300">{smtpResult.message}</span>
                ) : (
                  <span className="text-red-300">{smtpResult.error || JSON.stringify(smtpResult)}</span>
                )}
                {smtpResult.config_hint && (
                  <span className="block mt-1 text-[var(--color-text-muted)]">
                    Config: {JSON.stringify(smtpResult.config_hint)}
                  </span>
                )}
              </pre>
            )}
          </div>
        </Card>
      )}

      <Card className="editorial-panel">
        {loading ? (
          <div className="p-8 text-center"><Spinner /></div>
        ) : orgs.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={t('organizationsPage.emptyTitle')}
            description={t('organizationsPage.emptyDescription')}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/[0.03] border-b border-[var(--color-border)]">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('organizationsPage.table.name')}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('organizationsPage.table.id')}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('organizationsPage.table.users')}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('organizationsPage.table.signup')}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('organizationsPage.table.created')}
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('organizationsPage.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {orgs.map((org) => (
                  <tr key={org.id} className="hover:bg-white/[0.03]">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openOrg(org)}
                        className="flex items-center gap-3 text-left hover:text-primary-100 transition-colors"
                        data-testid={`open-org-${org.id}`}
                      >
                        <div className="w-10 h-10 bg-primary-200/12 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary-100" />
                        </div>
                        <span className="font-medium text-[var(--color-text)]">{org.name}</span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-[var(--color-text-muted)]">{org.id}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-sm">
                        <Users className="w-4 h-4" />
                        {org.user_count ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {org.public_signup_enabled ? (
                        <Badge variant="success">{t('organizationsPage.signupOpen')}</Badge>
                      ) : (
                        <Badge variant="info">{t('organizationsPage.signupClosed')}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--color-text-muted)]">
                      {formatDate(org.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {isMasterAdmin && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEdit(org)}
                              title={t('common.edit')}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteOrg(org)}
                              title={t('common.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Drill-down: open a gym */}
      <Modal
        isOpen={!!openOrgId}
        onClose={closeOrg}
        title={orgDetail?.name || t('organizationsPage.detail.loading')}
        size="lg"
      >
        {detailLoading ? (
          <div className="p-8 text-center"><Spinner /></div>
        ) : orgDetail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-[var(--color-text-muted)]">{t('organizationsPage.detail.orgId')}</p>
                <p className="font-mono text-xs break-all">{orgDetail.id}</p>
              </div>
              <div>
                <p className="text-[var(--color-text-muted)]">{t('organizationsPage.detail.created')}</p>
                <p>{formatDate(orgDetail.created_at)}</p>
              </div>
              <div>
                <p className="text-[var(--color-text-muted)]">{t('organizationsPage.detail.signup')}</p>
                <p>
                  {orgDetail.public_signup_enabled ? (
                    <Badge variant="success">{t('organizationsPage.signupOpen')}</Badge>
                  ) : (
                    <Badge variant="info">{t('organizationsPage.signupClosed')}</Badge>
                  )}
                </p>
              </div>
              <div>
                <p className="text-[var(--color-text-muted)]">{t('organizationsPage.detail.keycloakGroup')}</p>
                <p className="font-mono text-xs break-all">
                  {orgDetail.keycloak_group_path || '—'}
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-display text-lg uppercase text-[var(--color-text)] mb-2">
                {t('organizationsPage.detail.users')} ({orgUsers.length})
              </h4>
              {orgUsers.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t('organizationsPage.detail.noUsers')}
                </p>
              ) : (
                <div className="max-h-72 overflow-y-auto border border-[var(--color-border)] rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-white/[0.03] sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">
                          {t('organizationsPage.detail.nameCol')}
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">
                          {t('organizationsPage.detail.emailCol')}
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">
                          {t('organizationsPage.detail.roleCol')}
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-[var(--color-text-muted)]">
                          {t('organizationsPage.detail.statusCol')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-border)]">
                      {orgUsers.map((u) => (
                        <tr key={u.id}>
                          <td className="px-3 py-2">{u.name}</td>
                          <td className="px-3 py-2 font-mono text-xs text-[var(--color-text-muted)]">
                            {u.email}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="info">{u.role}</Badge>
                          </td>
                          <td className="px-3 py-2">
                            {u.is_active ? (
                              <Badge variant="success">{t('organizationsPage.detail.active')}</Badge>
                            ) : (
                              <Badge variant="warning">{t('organizationsPage.detail.inactive')}</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={!!editOrg}
        onClose={() => setEditOrg(null)}
        title={t('organizationsPage.edit.title')}
        size="md"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <Input
            label={t('auth.name')}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            required
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editPublicSignup}
              onChange={(e) => setEditPublicSignup(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">{t('organizationsPage.edit.publicSignup')}</span>
          </label>
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('organizationsPage.edit.publicSignupHint')}
          </p>
          {error && <p className="text-sm text-red-300">{error}</p>}
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setEditOrg(null)} className="flex-1">
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={editSubmitting || !editName.trim()} className="flex-1">
              {editSubmitting ? <Spinner size="sm" /> : t('common.save')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={!!deleteOrg}
        onClose={() => setDeleteOrg(null)}
        title={t('organizationsPage.delete.title')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm">
            {t('organizationsPage.delete.confirm', { name: deleteOrg?.name })}
          </p>
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('organizationsPage.delete.note')}
          </p>
          {error && <p className="text-sm text-red-300">{error}</p>}
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setDeleteOrg(null)} className="flex-1">
              {t('common.cancel')}
            </Button>
            <Button type="button" variant="danger" onClick={handleDelete} disabled={deleting} className="flex-1">
              {deleting ? <Spinner size="sm" /> : t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizationsPage;
